import React from "react";
import { keyframes } from "@emotion/react";
import { Toolbar, List, IconButton, Box, Tooltip } from "@mui/material";
import CloseMenuIcon from "../../assets/icons/close-menu.svg";
import { MainDashboardMenu } from "./DashboardMenuItems";
import logo from "../../assets/logos/3x-logo.png";
import smallLogo from "../../assets/logos/2x-initial-logo.png";
import { useTheme } from "@mui/material/styles";
import { BrandContext } from "../../App";
import ProfileSection from "../simple/ProfileSection";
import ProgressModal from "../simple/ProgressModal";
import { useNavigate } from "react-router-dom";

import { useAuth } from "../../contexts/AuthContext";

import useMediaQuery from '@mui/material/useMediaQuery';

const logoGlow = keyframes`
  0%   { box-shadow: 0 0 0 0 rgba(209, 199, 255, 0.8); }
  60%  { box-shadow: 0 0 0 10px rgba(209, 199, 255, 0.2); }
  100% { box-shadow: 0 0 0 14px rgba(209, 199, 255, 0); }
`;

export default function SideMenu({
  open,
  toggleDrawer,
  isMobile,
  toggleMobileMenu,
}) {
  const theme = useTheme();
  const { trainingProgram } = React.useContext(BrandContext);
  const { saveAuthToken, loggedInUser, logout } = useAuth();
  const navigate = useNavigate();

  const hideProgressModal = useMediaQuery('(max-height:876px)');

  const [showLogoHint, setShowLogoHint] = React.useState(false);

  React.useEffect(() => {
    if (!open && !localStorage.getItem("menuHintSeen")) {
      setShowLogoHint(true);
    }
  }, [open]);

  const handleLogoClick = () => {
    if (!open) {
      toggleDrawer();
      setShowLogoHint(false);
      localStorage.setItem("menuHintSeen", "1");
    }
  };

  return (
    <>
      <Toolbar
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: !open ? "center" : "space-between",
          margin: isMobile
            ? "1.5rem 1.5rem 1rem 2rem"
            : open
            ? `${theme.customSpacing.lg} 1.5rem}`
            : 0,
          p: 0,
        }}
      >
        <Box
          component="img"
          src={isMobile ? smallLogo : !open ? smallLogo : logo}
          alt="GuruNow Logo"
          onClick={handleLogoClick}
          onAnimationEnd={() => {
            setShowLogoHint(false);
            localStorage.setItem("menuHintSeen", "1");
          }}
          sx={{
            width: isMobile ? "45px" : open ? { xs: 45, sm: 56, md: 67 } : 45,
            cursor: !open ? "pointer" : "default",
            padding: 0,
            borderRadius: "8px",
            ...(showLogoHint && {
              animation: `${logoGlow} 1.2s ease-out 3`,
            }),
          }}
        />
        {open && !isMobile && (
          <IconButton onClick={toggleDrawer} sx={{ padding: 0 }}>
            <img src={CloseMenuIcon} alt="Close Menu" />
          </IconButton>
        )}
        {isMobile && (
          <IconButton
            onClick={() => toggleMobileMenu(false)}
            sx={{ padding: 0 }}
          >
            <svg
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g opacity="0.7">
                <path
                  d="M18 6L6 18M6 6L18 18"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </g>
            </svg>
          </IconButton>
        )}
      </Toolbar>
      <List component="nav" sx={{ margin: `0 ${theme.customSpacing.lg}` }}>
        <MainDashboardMenu open={open} toggleMobileMenu={toggleMobileMenu} />
      </List>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: theme.customSpacing.xl,
          position: "absolute",
          left: 0,
          right: 0,
          bottom: "2vh",
        }}
      >
        <Box
          sx={{
            padding: open ? "15px 12px 0 12px" : "0 10px",
            "& > button": {
              display: "flex",
              gap: "10px",
              alignItems: "center",
              background: "transparent",
              border: 0,
              color: "#fff",
              fontFamily: "Poppins, sans-serif",
              fontWeight: 600, // Semibold
              fontSize: "15px",
              cursor: "pointer",
              borderRadius: open ? "36px" : "6px",
              width: "100%",
              padding: open ? "15px 24px" : "12px 15px",
              textAlign: "center",
              justifyContent: open ? "flex-start" : "center",
              "&:hover": {
                color: "#01ECFF",
                background: "#021C64",
                "& svg path": {
                  stroke: "#01ECFF",
                },
              },
            },
            "& svg": {
              width: "20px",
              height: "20px",
            },
          }}
        >
          <Tooltip
            title={open ? "" : "Settings"}
            placement="right"
            slotProps={{
              tooltip: {
                sx: {
                  color: "#02002B",
                  background: "#D1C7FF",
                  "& span:before": {
                    background: "#D1C7FF",
                  },
                },
              },
            }}
            arrow
          >
            <button onClick={() => navigate("/user/settings")}>
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M2.5 6.66669L12.5 6.66669M12.5 6.66669C12.5 8.0474 13.6193 9.16669 15 9.16669C16.3807 9.16669 17.5 8.0474 17.5 6.66669C17.5 5.28598 16.3807 4.16669 15 4.16669C13.6193 4.16669 12.5 5.28598 12.5 6.66669ZM7.5 13.3334L17.5 13.3334M7.5 13.3334C7.5 14.7141 6.38071 15.8334 5 15.8334C3.61929 15.8334 2.5 14.7141 2.5 13.3334C2.5 11.9526 3.61929 10.8334 5 10.8334C6.38071 10.8334 7.5 11.9526 7.5 13.3334Z"
                  stroke="#FEFEFE"
                  strokeWidth="1.67"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              {open && <span>Settings</span>}
            </button>
          </Tooltip>
          {/* <button onClick={() => navigate("/user/admin")}>
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g clipPath="url(#clip0_279_2116)">
                <path
                  d="M10 12.5C11.3807 12.5 12.5 11.3807 12.5 10C12.5 8.61931 11.3807 7.50002 10 7.50002C8.61929 7.50002 7.5 8.61931 7.5 10C7.5 11.3807 8.61929 12.5 10 12.5Z"
                  stroke="#FEFEFE"
                  strokeWidth="1.67"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M15.6061 12.2727C15.5052 12.5012 15.4751 12.7547 15.5197 13.0005C15.5643 13.2462 15.6814 13.473 15.8561 13.6515L15.9015 13.697C16.0424 13.8377 16.1541 14.0048 16.2304 14.1887C16.3066 14.3727 16.3459 14.5698 16.3459 14.769C16.3459 14.9681 16.3066 15.1652 16.2304 15.3492C16.1541 15.5331 16.0424 15.7002 15.9015 15.8409C15.7608 15.9818 15.5937 16.0936 15.4098 16.1698C15.2258 16.2461 15.0287 16.2853 14.8295 16.2853C14.6304 16.2853 14.4333 16.2461 14.2493 16.1698C14.0654 16.0936 13.8983 15.9818 13.7576 15.8409L13.7121 15.7955C13.5336 15.6208 13.3068 15.5037 13.0611 15.4591C12.8153 15.4145 12.5618 15.4446 12.3333 15.5455C12.1093 15.6415 11.9182 15.801 11.7836 16.0042C11.649 16.2075 11.5767 16.4456 11.5758 16.6894V16.8182C11.5758 17.22 11.4161 17.6054 11.132 17.8896C10.8478 18.1737 10.4624 18.3334 10.0606 18.3334C9.65876 18.3334 9.27338 18.1737 8.98923 17.8896C8.70509 17.6054 8.54545 17.22 8.54545 16.8182V16.75C8.53959 16.4993 8.45842 16.2561 8.31251 16.0521C8.16659 15.8481 7.96268 15.6927 7.72727 15.6061C7.49878 15.5052 7.24531 15.4752 6.99955 15.5197C6.7538 15.5643 6.52703 15.6814 6.34848 15.8561L6.30303 15.9015C6.16231 16.0424 5.99521 16.1542 5.81127 16.2304C5.62734 16.3067 5.43017 16.3459 5.23106 16.3459C5.03195 16.3459 4.83478 16.3067 4.65085 16.2304C4.46691 16.1542 4.29981 16.0424 4.15909 15.9015C4.01822 15.7608 3.90646 15.5937 3.83021 15.4098C3.75396 15.2258 3.71472 15.0287 3.71472 14.8296C3.71472 14.6305 3.75396 14.4333 3.83021 14.2494C3.90646 14.0654 4.01822 13.8983 4.15909 13.7576L4.20454 13.7121C4.37919 13.5336 4.49635 13.3068 4.54091 13.0611C4.58547 12.8153 4.55539 12.5619 4.45454 12.3334C4.35851 12.1093 4.19906 11.9182 3.99581 11.7836C3.79256 11.649 3.55438 11.5768 3.31061 11.5758H3.18182C2.77997 11.5758 2.39459 11.4161 2.11044 11.132C1.8263 10.8479 1.66667 10.4625 1.66667 10.0606C1.66667 9.65878 1.8263 9.2734 2.11044 8.98925C2.39459 8.70511 2.77997 8.54548 3.18182 8.54548H3.25C3.50075 8.53961 3.74394 8.45844 3.94795 8.31253C4.15196 8.16661 4.30736 7.9627 4.39394 7.72729C4.49478 7.4988 4.52487 7.24533 4.48031 6.99957C4.43575 6.75382 4.31859 6.52705 4.14394 6.34851L4.09848 6.30305C3.95761 6.16233 3.84585 5.99523 3.76961 5.81129C3.69336 5.62736 3.65411 5.4302 3.65411 5.23108C3.65411 5.03197 3.69336 4.83481 3.76961 4.65087C3.84585 4.46693 3.95761 4.29983 4.09848 4.15911C4.2392 4.01824 4.40631 3.90648 4.59024 3.83023C4.77418 3.75398 4.97134 3.71474 5.17045 3.71474C5.36957 3.71474 5.56673 3.75398 5.75067 3.83023C5.9346 3.90648 6.10171 4.01824 6.24242 4.15911L6.28788 4.20457C6.46642 4.37922 6.69319 4.49637 6.93895 4.54093C7.1847 4.58549 7.43817 4.55541 7.66667 4.45457H7.72727C7.95134 4.35853 8.14244 4.19908 8.27704 3.99583C8.41164 3.79258 8.48388 3.5544 8.48485 3.31063V3.18184C8.48485 2.78 8.64448 2.39461 8.92863 2.11046C9.21277 1.82632 9.59816 1.66669 10 1.66669C10.4018 1.66669 10.7872 1.82632 11.0714 2.11046C11.3555 2.39461 11.5152 2.78 11.5152 3.18184V3.25002C11.5161 3.4938 11.5884 3.73197 11.723 3.93522C11.8576 4.13847 12.0487 4.29793 12.2727 4.39396C12.5012 4.4948 12.7547 4.52489 13.0004 4.48033C13.2462 4.43577 13.473 4.31861 13.6515 4.14396L13.697 4.09851C13.8377 3.95763 14.0048 3.84588 14.1887 3.76963C14.3727 3.69338 14.5698 3.65413 14.7689 3.65413C14.9681 3.65413 15.1652 3.69338 15.3492 3.76963C15.5331 3.84588 15.7002 3.95763 15.8409 4.09851C15.9818 4.23922 16.0935 4.40633 16.1698 4.59026C16.246 4.7742 16.2853 4.97136 16.2853 5.17048C16.2853 5.36959 16.246 5.56675 16.1698 5.75069C16.0935 5.93462 15.9818 6.10173 15.8409 6.24244L15.7955 6.2879C15.6208 6.46644 15.5036 6.69321 15.4591 6.93897C15.4145 7.18472 15.4446 7.43819 15.5455 7.66669V7.72729C15.6415 7.95136 15.8009 8.14246 16.0042 8.27706C16.2074 8.41166 16.4456 8.4839 16.6894 8.48487H16.8182C17.22 8.48487 17.6054 8.6445 17.8896 8.92865C18.1737 9.21279 18.3333 9.59818 18.3333 10C18.3333 10.4019 18.1737 10.7872 17.8896 11.0714C17.6054 11.3555 17.22 11.5152 16.8182 11.5152H16.75C16.5062 11.5161 16.268 11.5884 16.0648 11.723C15.8615 11.8576 15.7021 12.0487 15.6061 12.2727Z"
                  stroke="#FEFEFE"
                  strokeWidth="1.67"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </g>
              <defs>
                <clipPath id="clip0_279_2116">
                  <rect width="20" height="20" fill="white" />
                </clipPath>
              </defs>
            </svg>
            {open && <span>Team Management</span>}
          </button> */}
        </Box>
        {(!isMobile && hideProgressModal) || !hideProgressModal && (
          <ProgressModal
            trainingProgram={trainingProgram}
            sideMenuOpen={open}
          />
        )}
        <ProfileSection
          open={open}
          loggedInUser={loggedInUser}
          logout={logout}
          navigate={navigate}
        />
      </Box>
    </>
  );
}
